/**
 * CreateExternalUserForm
 * Reusable form for creating external users within modals
 *
 * Used by: DelegateModal, CreateWorkOrderModal, AssignWorkOrderModal, ShareLinkModal
 */
import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { Plus } from 'lucide-react';
import { useExternalUsers } from '../hooks/useExternalUsers';
import { ExternalUser, CreateExternalUserPayload } from '../types/guestAccess';

interface CreateExternalUserFormProps {
    /** Called when user is successfully created */
    onUserCreated: (user: ExternalUser) => void;
    /** Called when user clicks "Back" */
    onCancel: () => void;
}

/**
 * Form component for creating external users
 * Renders Modal.Body and Modal.Footer - wrap in a Modal with appropriate header
 */
const CreateExternalUserForm: React.FC<CreateExternalUserFormProps> = ({
    onUserCreated,
    onCancel,
}) => {
    const { createUser, submitting } = useExternalUsers();

    const [error, setError] = useState<string | null>(null);
    const [form, setForm] = useState<CreateExternalUserPayload>({
        name: '',
        email: '',
        phone: '',
        company: '',
        role: '',
    });

    const handleSubmit = async () => {
        if (!form.name.trim() || !form.email.trim()) {
            setError('Namn och e-post krävs');
            return;
        }

        setError(null);

        try {
            const uid = await createUser(form);

            // Create the ExternalUser object
            const newUser: ExternalUser = {
                uid,
                name: form.name,
                email: form.email,
                phone: form.phone || undefined,
                company: form.company || undefined,
                role: form.role || undefined,
                isExternal: true,
                createdAt: new Date().toISOString(),
                createdBy: '',
            };

            onUserCreated(newUser);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Kunde inte skapa användaren';
            setError(message);
        }
    };

    const updateField = (field: keyof CreateExternalUserPayload, value: string) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    return (
        <>
            <Modal.Body>
                {error && (
                    <Alert variant="danger" dismissible onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>Namn *</Form.Label>
                        <Form.Control
                            type="text"
                            value={form.name}
                            onChange={(e) => updateField('name', e.target.value)}
                            placeholder="Anna Andersson"
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>E-post *</Form.Label>
                        <Form.Control
                            type="email"
                            value={form.email}
                            onChange={(e) => updateField('email', e.target.value)}
                            placeholder="anna@foretag.se"
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Telefon</Form.Label>
                        <Form.Control
                            type="tel"
                            value={form.phone}
                            onChange={(e) => updateField('phone', e.target.value)}
                            placeholder="070-123 45 67"
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Företag</Form.Label>
                        <Form.Control
                            type="text"
                            value={form.company}
                            onChange={(e) => updateField('company', e.target.value)}
                            placeholder="T.ex. ABC Bygg AB"
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Roll</Form.Label>
                        <Form.Control
                            type="text"
                            value={form.role}
                            onChange={(e) => updateField('role', e.target.value)}
                            placeholder="T.ex. rörmokare"
                        />
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onCancel}>
                    Tillbaka
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={submitting}
                >
                    {submitting ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Skapar...
                        </>
                    ) : (
                        <>
                            <Plus size={18} className="me-2" />
                            Skapa användare
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </>
    );
};

export default CreateExternalUserForm;
